import { createSlice } from "@reduxjs/toolkit"

const initialState = {
    currentEditedSection: null,
    isEditorMode: false,
    clickedSection: null,
    currentLang: null,
    pageInfo: {},
    pageData: {},
    isDirty: null,
    originalPageData: null,
    originalPageInfo: null,
    clickedSectionId: null  
}

// Helper: recalc isDirty based on original data
const recalcDirty = (state) => {
    if (!state.originalPageData && !state.originalPageInfo) {
        state.isDirty = false
        return
    }
    const dataSame = JSON.stringify(state.pageData) === JSON.stringify(state.originalPageData)
    const infoSame = JSON.stringify(state.pageInfo) === JSON.stringify(state.originalPageInfo)
    state.isDirty = !(dataSame && infoSame)
}

const pageSlice = createSlice({
    name: "pages",
    initialState,
    reducers: {
        // ---- set initial state (used after loading data) ----
        setInitialPageState(state, action) {
            const { pageData, pageInfo } = action.payload
            state.pageData = pageData
            state.pageInfo = pageInfo
            state.originalPageData = JSON.parse(JSON.stringify(pageData))
            state.originalPageInfo = JSON.parse(JSON.stringify(pageInfo))
            state.isDirty = false
        },

        // ---- existing reducers (modified to recalc dirty) ----
        updateCurrentEditedSection(state, action) {
            state.currentEditedSection = action.payload
        },
        updateSetIsEditorMode(state, action) {
            state.isEditorMode = action.payload
        },
		updateClickedSection(state, action) {
			state.clickedSection = action.payload   
			state.clickedSectionId = action.payload 
		},
        updateCurrentLang(state, action) {
            state.currentLang = action.payload
        },
        updatePageInfo(state, action) {
            state.pageInfo = action.payload
            recalcDirty(state)
        },
        updatePageData(state, action) {
            state.pageData = action.payload
            recalcDirty(state)
        },
        updatePageSection(state, action) {
            const { data: newData, index } = action.payload
            const currentLang = state.currentLang
            if (state.pageData[currentLang]) {
                state.pageData[currentLang][index].data = newData
                recalcDirty(state)
            }
        },
        updatePageAdvancedSettings(state, action) {
            const { data: newData, index } = action.payload
            const currentLang = state.currentLang
            if (state.pageData[currentLang]) {
                state.pageData[currentLang][index].advanced = newData
                recalcDirty(state)
            }
        },
        updateCaseStudySection(state, action) {
            const { data: newData, index } = action.payload
            const currentLang = state.currentLang
            if (state.pageData[currentLang]) {
                state.pageData[currentLang][index].data = newData
                recalcDirty(state)
            }
        },
        updateCaseStudySectionsData(state, action) {
            const { data: newData, index } = action.payload
            const currentLang = state.currentLang
            if (state.pageData[currentLang]) {
                state.pageData[currentLang][index].data = newData
                recalcDirty(state)
            }
        },
        updateCaseStudyDetails(state, action) {
            const { data: newData, index } = action.payload
            const currentLang = state.currentLang
            if (state.pageData[currentLang]) {
                state.pageData[currentLang][index].data = newData
                recalcDirty(state)
            }
        },
        updatePageTitle(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].title = newData
                recalcDirty(state)
            }
        },
        updatePageHeaderLayout(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].header_layout = newData
                recalcDirty(state)
            }
        },
        updatePageHeaderShowShoppingCart(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].is_show_shopping_cart = newData
                recalcDirty(state)
            }
        },
        updatePageFooterLayout(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].footer_layout = newData
                recalcDirty(state)
            }
        },
        updatePageSubTitle(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].sub_title = newData
                recalcDirty(state)
            }
        },
        updatePageDetails(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].details = newData
                recalcDirty(state)
            }
        },
        updatePageThumbnailImage(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].thumbnail_image = newData
                recalcDirty(state)
            }
        },
        updatePageInfoThumbnailImage(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].info_thumbnail_image = newData
                recalcDirty(state)
            }
        },
        updatePagePageTitle(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].page_title = newData
                recalcDirty(state)
            }
        },
        updatePagePageSubTitle(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].page_sub_title = newData
                recalcDirty(state)
            }
        },
        updatePageProjectInfoText(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].project_info_text = newData
                recalcDirty(state)
            }
        },
        updatePageProjectInfo(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].projectInfo = newData
                recalcDirty(state)
            }
        },
        updatePageIconBoxTitle(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].icon_box_title = newData
                recalcDirty(state)
            }
        },
        updatePageIconBoxSubTitle(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].icon_box_sub_title = newData
                recalcDirty(state)
            }
        },
        updatePageIconBox(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].icon_box = newData
                recalcDirty(state)
            }
        },
        updatePageInfoTitle(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].info_title = newData
                recalcDirty(state)
            }
        },
        updatePageInfoList(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].info_list = newData
                recalcDirty(state)
            }
        },
        updatePagePageCategory(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].category = newData
                recalcDirty(state)
            }
        },
        updatePageBreadcrumb(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].is_show_breadcrumb = newData
                recalcDirty(state)
            }
        },
        updatePageBreadcrumbImage(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].breadcrumb_image = newData
                recalcDirty(state)
            }
        },
        updatePageBreadcrumbTitle(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].breadcrumb_title = newData
                recalcDirty(state)
            }
        },
        updatePageHeaderActionButtonText(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].header_action_button_text = newData
                recalcDirty(state)
            }
        },
        updatePageHeaderActionButtonURL(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].header_action_button_url = newData
                recalcDirty(state)
            }
        },
        updatePageDescription(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].description = newData
                recalcDirty(state)
            }
        },
        updatePageMetaTitle(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].meta_title = newData
                recalcDirty(state)
            }
        },
        updatePageMetaDescription(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].meta_description = newData
                recalcDirty(state)
            }
        },
        updatePageMetaTags(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].meta_tags = newData
                recalcDirty(state)
            }
        },
        updatePageMetaImage(state, action) {
            const newData = action.payload
            const currentLang = state.currentLang
            if (state.pageInfo[currentLang]) {
                state.pageInfo[currentLang].meta_image = newData
                recalcDirty(state)
            }
        },
        updatePageHeroSection(state, action) {
            const { data: newData, index } = action.payload
            const currentLang = state.currentLang
            if (state.pageData[currentLang]) {
                state.pageData[currentLang][index].data = newData
                recalcDirty(state)
            }
        },
        updatePageServiceAdvancedSettings(state, action) {
            const { data: newData, index } = action.payload
            const currentLang = state.currentLang
            if (state.pageData[currentLang]) {
                state.pageData[currentLang][index].advanced = newData
                recalcDirty(state)
            }
        },
        updatePageCoreValueAdvancedSettings(state, action) {
            const { data: newData, index } = action.payload
            const currentLang = state.currentLang
            if (state.pageData[currentLang]) {
                state.pageData[currentLang][index].advanced = newData
                recalcDirty(state)
            }
        },
        updatePageSectionsData(state, action) {
            const { data: newData, index } = action.payload
            const currentLang = state.currentLang
            if (state.pageData[currentLang]) {
                state.pageData[currentLang][index].data = newData
                recalcDirty(state)
            }
        },
        clearState(state) {
            state.pageData = {}
            state.pageInfo = {}
            state.originalPageData = null
            state.originalPageInfo = null
            state.isDirty = false
        },
		setDirty(state) {
			console.log("🔥 setDirty called! Previous:", state.isDirty);
			state.isDirty = true;
			console.log("🔥 setDirty new value:", state.isDirty);
		},
				setClean(state) {
					state.isDirty = false
				}
				
			}
		})

export default pageSlice.reducer
export const {
    setInitialPageState,
    updateCurrentEditedSection,
    updateSetIsEditorMode,
    updateClickedSection,
    updateCurrentLang,
    updatePageInfo,
    updatePageData,
    updatePageSection,
    updatePageAdvancedSettings,
    updateCaseStudySection,
    updateCaseStudySectionsData,
    updateCaseStudyDetails,
    updatePageTitle,
    updatePageHeaderLayout,
    updatePageHeaderShowShoppingCart,
    updatePageFooterLayout,
    updatePageSubTitle,
    updatePageDetails,
    updatePageThumbnailImage,
    updatePageInfoThumbnailImage,
    updatePagePageTitle,
    updatePagePageSubTitle,
    updatePageProjectInfoText,
    updatePageProjectInfo,
    updatePageIconBoxTitle,
    updatePageIconBoxSubTitle,
    updatePageIconBox,
    updatePageInfoTitle,
    updatePageInfoList,
    updatePagePageCategory,
    updatePageBreadcrumb,
    updatePageBreadcrumbImage,
    updatePageBreadcrumbTitle,
    updatePageHeaderActionButtonText,
    updatePageHeaderActionButtonURL,
    updatePageDescription,
    updatePageMetaTitle,
    updatePageMetaDescription,
    updatePageMetaTags,
    updatePageMetaImage,
    updatePageHeroSection,
    updatePageServiceAdvancedSettings,
    updatePageCoreValueAdvancedSettings,
    updatePageSectionsData,
    clearState,
    setDirty,
    setClean
} = pageSlice.actions